import React, { useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  FormText,
  Input,
  Row,
} from 'reactstrap'

import CharCheckModal from '../../components/molecules/CharCheckModal'
import GPTEstimateModal from '../../components/molecules/GPTEstimateModal'
import { ToastContainer } from 'react-toastify'
import { notify } from '../../utils/notify'
import FileInput from '../../components/molecules/FileInput'
import ClienteSelect from '../../components/molecules/ClienteSelect'
import { useFileUpload } from '../../hooks'
import { api } from '../../utils'

const DEFAULT_PROMPT = `Sei un SEO Copywriter esperto, devi individuare alcuni topic sui quali scrivere dei contenuti e relativo brief da fornire ai copy comprensivo di: introduzione iniziale, idee di titolo per i paragrafi e abstract per la scrittura. L'abstract deve fungere da guida per chi scrivera il contenuto dovra quindi avere tutte le informazioni che sara poi utile inserire e approfondire nel testo finale.
I topic che fornirai hanno lo scopo di guidare alla scrittura di contenuti editoriali con l'obiettivo di migliorare la visibilita SEO per le keyword informazionali.
Per permetterti di avere contesto e capire quali sono i contenuti che si posizionano meglio durante le ricerche ti daro in input il title, la description, gli heading tag presenti in pagina (sottotitoli di paragrafi) e il contenuto testuale per le prime 10 posizioni di Google per la ricerca '{keyword}'.

Razionalizza e riassumi le informazioni fornite, crea una bozza di topic sui quali posso scrivere contenuti legati al tema, esamina la bozza che hai fatto e recupera solamente i due topic piu pertinenti al contesto che ti ho fornito.
Ragiona ed elabora paragrafi e sezioni utilizzando uno stile "{style}" e con un tone of voice "{tone_of_voice}".

L'output dev'essere cosi strutturato:
1 Titolo dell'articolo (da inserire nel campo topic)
2 Obiettivo dell'Articolo (da inserire nel campo introduction). Descrivi in modo chiaro e conciso lo scopo principale dell'articolo. Specifica il tipo di informazioni che il lettore dovrebbe ottenere leggendo l'articolo.
3 Keyword secondarie (da inserire nel campo secondary_keywords). In questo sottoinsieme fornisci 5/7 keyword secondarie inerenti all'articolo che devono essere incluse a fini dell'ottimizzazione SEO separate da un a capo.
4 Domande a cui rispondere (da inserire nel campo questions). Elenca le domande principali a cui l'articolo dovrebbe rispondere. Questo aiuta il copywriter a capire quali informazioni devono essere incluse.
5 Struttura dell'Articolo (da inserire nel campo abstract). Definisci la struttura generale dell'articolo tramite questi sottoinsiemi (da fornire separati da un a capo e con nome del sottoinsieme):
Introduzione dell'articolo: parte introduttiva dell'articolo che riassume il contenuto che verra affrontato nei paragrafi sottostanti.
Sezioni principali: proponi un numero indicativo di idee di titolo e paragrafi, con informazioni dettagliate ed approfondite (almeno qualche riga di spiegazione del paragrafo), questo elemento e il core dell'abstract e servira da guida per la scrittura, aggiungi indicazioni e contenuto guida possibile per aiutare il copy nella scrittura del contenuto.

Il risultato deve essere in formato JSON, piu precisamente una lista di dizionari. Il JSON dovra essere un TopicsArray (presentato come typescript interface):

\`\`\`
interface TopicsObject {
    topic: string; // titolo del topic
    introduction: string; // descrizione del topic
    secondary_keywords: string; // keyword secondarie
    questions: string; // domande a cui rispondere
    abstract: string; // indicazioni per i copy sui contenuti dell'articolo
}

interface TopicsArray {
    items: TopicsObject[];
}
\`\`\``

const PEDForm = () => {
  const [file, setFile] = useState(null)
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT)
  const [style, setStyle] = useState('Neutro')
  const [toneOfVoice, setToneOfVoice] = useState('Colloquiale')
  const [clienti, setClienti] = useState([])
  const [selectedClientId, setSelectedClientId] = useState('')
  const { upload, loading } = useFileUpload()

  // Modal check caratteri
  const [showCharCheck, setShowCharCheck] = useState(false)
  const [errCharList, setErrCharList] = useState([])
  const [acceptedCharCheck, setAcceptedCharCheck] = useState(false)

  // Modal check gpt
  const [showGptCheck, setShowGptCheck] = useState(false)
  const [gptEstimateParams, setGptEstimateParams] = useState({
    n_keywords: -1,
    total_tokens: -1,
    estimate: -1,
  })
  const [acceptedGptCheck, setAcceptedGptCheck] = useState(false)

  const handleSubmit = async (event) => {
    if (event) {
      event.preventDefault()
    }

    if (file == null) {
      notify('File mancante', 'error')
      return
    }

    if (prompt && !prompt.includes('{keyword}')) {
      notify('Il custom prompt deve contenere la variabile {keyword}', 'error')
      return
    }

    if (!prompt.trim()) {
      notify('Il custom prompt e obbligatorio', 'error')
      return
    }
    if (!selectedClientId) {
      notify('Seleziona un cliente.', 'error')
      return
    }

    const resolvedPrompt = (prompt || '')
      .replaceAll('{style}', String(style || '').toLowerCase())
      .replaceAll('{tone_of_voice}', String(toneOfVoice || '').toLowerCase())

    const formData = new FormData()
    formData.append('file', file)
    formData.append('prompt', resolvedPrompt)
    formData.append('style', style)
    formData.append('tone_of_voice', toneOfVoice)
    formData.append('gpt_estimate_accepted', acceptedGptCheck)
    formData.append('cliente', selectedClientId)

    try {
      await upload('post', 'ped/', formData, {
        onSuccess: (data) => {
          if (typeof data !== 'undefined') {
            notify("Richiesta mandata. Riceverai una mail al termine dell'elaborazione", 'success')
            setAcceptedCharCheck(false)
            setAcceptedGptCheck(false)
            setFile(null)
          } else {
            notify(
              'Richiesta fallita. Qualcosa e andato storto, riprovare o contattare un Innovation',
              'error'
            )
          }
        },
        onError: (errorMessage, err) => {
          if (err.response?.data?.message?.includes('ERROR Encoding keywords')) {
            setErrCharList(err.response.data?.errors)
            setShowCharCheck(true)
          } else if (err.response?.data?.message?.includes('GPT Estimate')) {
            setGptEstimateParams(err.response.data?.estimates)
            setShowGptCheck(true)
          } else {
            notify(err.response?.data?.message || err.response?.data, 'error')
            console.log(err.response?.data)
            setFile(null)
            setAcceptedCharCheck(false)
            setAcceptedGptCheck(false)
          }
        },
      })
    } catch (err) {
      console.error('PED Generator failed:', err)
    }
  }

  const confirmEvaluation = (e) => {
    e.preventDefault()
    setShowCharCheck(false)
    setAcceptedCharCheck(true)
  }

  const cancelEvaluation = () => {
    setShowCharCheck(false)
    setAcceptedGptCheck(false)
  }

  const confirmEstimate = (e) => {
    e.preventDefault()
    setShowGptCheck(false)
    setAcceptedGptCheck(true)
  }

  const cancelEstimate = () => {
    setShowGptCheck(false)
    setShowCharCheck(false)
  }

  useEffect(() => {
    api
      .get('clienti/')
      .then((response) => {
        setClienti(Array.isArray(response.data) ? response.data : response.data?.results || [])
      })
      .catch((err) => {
        console.error('Failed to fetch clienti', err)
      })
  }, [])

  useEffect(() => {
    if (acceptedCharCheck) {
      handleSubmit()
    }
  }, [acceptedCharCheck])

  useEffect(() => {
    if (acceptedGptCheck) {
      handleSubmit()
    }
  }, [acceptedGptCheck])

  return (
    <>
      <Row>
        <div className="col pb-5">
          <Card className="bg-secondary shadow">
            <CardHeader className="bg-white border-0">
              <h1 className="mb-0">PED Generator</h1>
            </CardHeader>
            <CardBody>
              <CharCheckModal
                show={showCharCheck}
                onHide={cancelEvaluation}
                onSubmit={confirmEvaluation}
                errCharList={errCharList}
                withExclusion={true}
              />
              <GPTEstimateModal
                show={showGptCheck}
                onHide={cancelEstimate}
                onSubmit={confirmEstimate}
                params={gptEstimateParams}
              />
              {loading ? (
                <Spinner animation="grow" />
              ) : (
                <Form onSubmit={handleSubmit} id="idea-form" action="">
                  <FormGroup row>
                    <Col md={6}>
                      <ClienteSelect
                        id="select-client"
                        label="Cliente canonico"
                        clienti={clienti}
                        selectedClientId={selectedClientId}
                        onChange={setSelectedClientId}
                        isRequired
                        helpText="Il job PED viene associato al record canonico del cliente."
                        showCreateLink
                      />
                    </Col>
                  </FormGroup>
                  <FormGroup row>
                    <Col md={6}>
                      <FileInput
                        onFileChange={setFile}
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                        helpText={
                          <>
                            Carica un file contenente le tue keywords da verificare. <br />
                            <b>Il file deve contenere la colonna "Keyword"</b>
                          </>
                        }
                        required={true}
                      />
                    </Col>
                  </FormGroup>
                  <FormGroup row>
                    <Col md={12}>
                      <label htmlFor="custom-prompt" className="form-control-label">
                        Custom prompt
                      </label>
                      <Input
                        className="form-control-alternative"
                        id={'custom-prompt'}
                        type={'textarea'}
                        rows="5"
                        style={{ border: '0' }}
                        value={prompt}
                        placeholder={'Sovrascrivi il prompt di default. Deve contenere {keyword}.'}
                        onChange={(event) => {
                          setPrompt(event.target.value)
                        }}
                      />
                      <FormText color="muted">
                        Non modificare per usare il prompt standard. Altrimenti, includi{' '}
                        {'{keyword}'}.
                      </FormText>
                    </Col>
                  </FormGroup>
                  <FormGroup row>
                    <Col md={6}>
                      <label htmlFor="style" className="form-control-label">
                        Stile
                      </label>
                      <Input
                        className="form-control-alternative"
                        id={'text-stile'}
                        type={'text'}
                        style={{ border: '0' }}
                        value={style}
                        placeholder={'Definisci uno stile'}
                        required
                        onChange={(event) => {
                          setStyle(event.target.value)
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <label htmlFor="tone-of-voice" className="form-control-label">
                        Tone of voice
                      </label>
                      <Input
                        className="form-control-alternative"
                        id={'text-tone-of-voice'}
                        type={'text'}
                        style={{ border: '0' }}
                        value={toneOfVoice}
                        placeholder={'Definisci il tone of voice'}
                        required
                        onChange={(event) => {
                          setToneOfVoice(event.target.value)
                        }}
                      />
                    </Col>
                  </FormGroup>
                  <Row style={{ display: 'flex', justifyContent: 'flex-start', paddingLeft: 11 }}>
                    <Button color="info" size="lg" onClick={handleSubmit}>
                      Invia
                    </Button>
                  </Row>
                </Form>
              )}
              <ToastContainer />
            </CardBody>
          </Card>
        </div>
      </Row>
    </>
  )
}

export default PEDForm
